import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { instructorsService } from "api/services/instructors.service";
import { tourApprovalsService } from "api/services/tour-approvals.service";
import { toursService } from "api/services/tours.service";
import ContentHeader from "components/Layout/ContentHeader";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import RichTextEditor from "components/UI/RichTextEditor";

import type { InstructorListItem } from "api/types/instructor.types";
import type { TourApprovalDetail } from "api/types/tour-approval.types";
import type {
  TourDetail,
  TourLegacyImage,
  TourUpdateRequest,
  TourScheduleDay,
  TourIncludeItem,
} from "api/types/tours.types";
import type { RichTextChangeSource } from "components/UI/RichTextEditor";
import "pages/AdminPage.scss";
import "./TourEdit.scss";

// ─────────────────────────────────────────────
// 상수 / 타입
// ─────────────────────────────────────────────
// 진입 출처: 승인 대기/완료는 상세 조회 엔드포인트가 다르다.
//  - approval : /travel-products-approvals/{id}/   (승인 대기 포함 모든 상태, 어드민 전용)
//  - product  : /travel-products/{id}/             (승인 완료 투어)
// 단, 저장(update)은 양쪽 모두 동일한 /travel-products/{id}/update/ 사용.
type TourSource = "approval" | "product";

const CURRENCY_OPTIONS = [
  { value: "KRW", label: "KRW" },
  { value: "USD", label: "USD" },
  { value: "JPY", label: "JPY" },
];

interface TourEditForm {
  name: string;
  location: string;
  start_date: string;
  end_date: string;
  price: string;
  currency: string;
  balance_usd: string;
  balance_currency: string;
  single_room_surcharge: string;
  min_participants: string;
  max_participants: string;
  intro_desc: string;
  detailed_content: string;
  qualifications: string;
  guide_names: string[];
}

const EMPTY_FORM: TourEditForm = {
  name: "",
  location: "",
  start_date: "",
  end_date: "",
  price: "",
  currency: "KRW",
  balance_usd: "",
  balance_currency: "USD",
  single_room_surcharge: "0",
  min_participants: "1",
  max_participants: "",
  intro_desc: "",
  detailed_content: "",
  qualifications: "",
  guide_names: [],
};

const toStr = (v: string | number | null | undefined) => (v == null ? "" : String(v));

const toDate = (v: string | null | undefined) => (v ? v.slice(0, 10) : "");

/**
 * legacy(`TravelProductImage`) 대표 이미지 — **표시 fallback 전용**.
 *
 * BE `get_thumbnail_image` 는 `images[0]` 이 없을 때 legacy 로 폴백한다. 다만 상세
 * serializer 가 `is_thumbnail` 을 노출하지 않으므로 활성 이미지 중 `order` 최솟값을 쓴다.
 * legacy 는 `TourUpdateSerializer` 에 쓰기 필드가 없어 저장 payload 에 절대 싣지 않는다.
 */
const pickLegacyThumbnail = (images: TourLegacyImage[] | undefined): string | null => {
  const active = (images ?? []).filter((img) => img.is_active && img.image);
  if (active.length === 0) return null;
  return active.slice().sort((a, b) => a.order - b.order)[0].image;
};

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const TourEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const source: TourSource = searchParams.get("source") === "approval" ? "approval" : "product";

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tourName, setTourName] = useState("");
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [form, setForm] = useState<TourEditForm>(EMPTY_FORM);
  const [schedule, setSchedule] = useState<TourScheduleDay[]>([]);
  const [includeItems, setIncludeItems] = useState<TourIncludeItem[]>([]);
  const [instructors, setInstructors] = useState<InstructorListItem[]>([]);
  const [guideToAdd, setGuideToAdd] = useState("");

  // 대표 썸네일: images[0] 이 대표, 나머지(images[1:])는 이 화면에서 편집하지 않고 보존한다.
  const [images, setImages] = useState<string[]>([]);
  const [legacyThumbnail, setLegacyThumbnail] = useState<string | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // 저장 재진입 가드 — `saving` state 는 리렌더 이후에야 버튼을 막으므로 동기 ref 로 차단한다.
  const savingRef = useRef(false);

  // 진행 중인 본문 이미지 업로드 수. ref 는 handleSave 동기 차단용, state 는 버튼 disabled 용.
  const detailImageUploadingRef = useRef(0);
  const [detailImageUploading, setDetailImageUploading] = useState(0);

  // 상세 조회 요청 시퀀스. route 전환으로 요청이 겹칠 때, 뒤늦게 도착한 이전 요청이
  // 현재 화면 상태를 덮지 않도록 "가장 최근 요청"만 반영한다.
  const requestSeqRef = useRef(0);

  // 상세 내용을 **사용자가** 편집했는지. Quill 은 마운트 시 HTML 을 정규화하고 그 변경을
  // source="api" 로 알리는데, 이를 편집으로 오인하면 화면을 열기만 해도 본문이 덮어써진다.
  // 따라서 source==="user" 인 변경만 dirty 로 보고, 아니면 PATCH 에서 키를 생략한다.
  const detailedContentDirtyRef = useRef(false);

  // ─────────────────────────────────────────
  // 데이터 조회
  // ─────────────────────────────────────────
  useEffect(() => {
    // 같은 인스턴스에서 route id/source 만 바뀌면 컴포넌트가 언마운트되지 않는다.
    // 이전 투어에서 고른 썸네일 파일이 남아 새 투어에 업로드되지 않도록 즉시 비운다.
    setThumbnailFile(null);

    // 이 실행분의 요청 번호. 언마운트/재실행 후 도착한 응답은 전부 버린다.
    const seq = ++requestSeqRef.current;
    let cancelled = false;
    const isStale = () => cancelled || seq !== requestSeqRef.current;

    if (!id) return;
    const pk = Number(id);
    if (isNaN(pk)) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetcher =
      source === "approval"
        ? tourApprovalsService.get(pk).then((res) => mapApproval(res.data))
        : toursService.get(pk).then((res) => mapProduct(res.data as TourDetail));

    fetcher
      .then(
        ({
          form: f,
          name,
          approved,
          schedule: sch,
          include_items: inc,
          images: imgs,
          legacy_thumbnail: legacy,
        }) => {
          if (isStale()) return;
          setForm(f);
          setTourName(name);
          setIsApproved(approved);
          setSchedule(Array.isArray(sch) ? sch : []);
          setIncludeItems(Array.isArray(inc) ? inc : []);
          setImages(Array.isArray(imgs) ? imgs : []);
          setLegacyThumbnail(legacy);
          detailedContentDirtyRef.current = false;
        },
      )
      .catch(() => {
        if (isStale()) return;
        setNotFound(true);
      })
      .finally(() => {
        if (isStale()) return;
        setLoading(false);
      });

    // 강사 목록 (인솔강사 선택용)
    instructorsService
      .list()
      .then((res) => {
        if (isStale()) return;
        setInstructors(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (isStale()) return;
        setInstructors([]);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, source]);

  // ─────────────────────────────────────────
  // 응답 → 폼 매핑
  // ─────────────────────────────────────────
  const mapApproval = (data: TourApprovalDetail) => ({
    name: data.name,
    approved: data.is_approved,
    schedule: data.schedule ?? [],
    include_items: data.include_items ?? [],
    images: data.images ?? [],
    // 승인 상세(TourApprovalSerializer)는 legacy_images 를 내려주지 않는다.
    legacy_thumbnail: null as string | null,
    form: {
      name: toStr(data.name),
      location: toStr(data.location),
      start_date: toDate(data.start_date),
      end_date: toDate(data.end_date),
      price: toStr(data.price),
      currency: data.currency || "KRW",
      balance_usd: toStr(data.balance_usd),
      balance_currency: data.balance_currency || "USD",
      single_room_surcharge: toStr(data.single_room_surcharge) || "0",
      min_participants: toStr(data.min_participants),
      max_participants: toStr(data.max_participants),
      intro_desc: toStr(data.intro_desc),
      detailed_content: toStr(data.detailed_content),
      qualifications: toStr(data.qualifications),
      guide_names: data.guide_names ?? [],
    } as TourEditForm,
  });

  const mapProduct = (data: TourDetail) => ({
    name: data.name,
    approved: true,
    schedule: data.schedule ?? [],
    include_items: data.include_items ?? [],
    images: data.images ?? [],
    legacy_thumbnail: pickLegacyThumbnail(data.legacy_images),
    form: {
      name: toStr(data.name),
      location: toStr(data.location),
      start_date: toDate(data.start_date),
      end_date: toDate(data.end_date),
      price: toStr(data.price),
      currency: data.currency || "KRW",
      balance_usd: toStr(data.balance_usd),
      balance_currency: data.balance_currency || "USD",
      single_room_surcharge: toStr(data.single_room_surcharge) || "0",
      min_participants: toStr(data.min_participants),
      max_participants: toStr(data.max_participants),
      intro_desc: toStr(data.intro_desc),
      detailed_content: toStr(data.detailed_content),
      qualifications: toStr(data.qualifications),
      guide_names: (data.guides ?? []).map((g) => g.name),
    } as TourEditForm,
  });

  // ─────────────────────────────────────────
  // 폼 헬퍼
  // ─────────────────────────────────────────
  const setField = <K extends keyof TourEditForm>(key: K, value: TourEditForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const addGuide = (name: string) => {
    if (!name || form.guide_names.includes(name)) return;
    if (form.guide_names.length >= 5) {
      alert("인솔강사는 최대 5명까지 지정할 수 있습니다.");
      return;
    }
    setField("guide_names", [...form.guide_names, name]);
  };

  const removeGuide = (name: string) =>
    setField(
      "guide_names",
      form.guide_names.filter((n) => n !== name),
    );

  // 일정: 날짜 재번호 (삭제/추가 후 Day 순번 유지)
  const renumberDays = (days: TourScheduleDay[]) => days.map((d, i) => ({ ...d, day: i + 1 }));

  // 일정: 날짜 / 항목 텍스트 수정
  const setScheduleDate = (dayIdx: number, date: string) =>
    setSchedule((prev) => prev.map((d, i) => (i === dayIdx ? { ...d, date } : d)));

  const setScheduleItem = (dayIdx: number, itemIdx: number, text: string) =>
    setSchedule((prev) =>
      prev.map((d, i) =>
        i === dayIdx ? { ...d, items: d.items.map((it, j) => (j === itemIdx ? text : it)) } : d,
      ),
    );

  // 일정: 날짜(Day) 추가 / 삭제
  const addScheduleDay = () =>
    setSchedule((prev) => renumberDays([...prev, { day: prev.length + 1, date: "", items: [""] }]));

  const removeScheduleDay = (dayIdx: number) =>
    setSchedule((prev) => renumberDays(prev.filter((_, i) => i !== dayIdx)));

  // 일정: 세부 항목 추가 / 삭제
  const addScheduleItem = (dayIdx: number) =>
    setSchedule((prev) =>
      prev.map((d, i) => (i === dayIdx ? { ...d, items: [...d.items, ""] } : d)),
    );

  const removeScheduleItem = (dayIdx: number, itemIdx: number) =>
    setSchedule((prev) =>
      prev.map((d, i) =>
        i === dayIdx ? { ...d, items: d.items.filter((_, j) => j !== itemIdx) } : d,
      ),
    );

  // 포함/불포함: 토글 / 라벨 수정 / 추가 / 삭제
  const toggleIncludeItem = (idx: number) =>
    setIncludeItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, include: !it.include } : it)),
    );

  const setIncludeLabel = (idx: number, label: string) =>
    setIncludeItems((prev) => prev.map((it, i) => (i === idx ? { ...it, label } : it)));

  const addIncludeItem = () => setIncludeItems((prev) => [...prev, { label: "", include: true }]);

  const removeIncludeItem = (idx: number) =>
    setIncludeItems((prev) => prev.filter((_, i) => i !== idx));

  // ─────────────────────────────────────────
  // 대표 썸네일 / 상세 본문 이미지
  // ─────────────────────────────────────────
  // 선택한 파일의 로컬 미리보기. 파일 교체·언마운트 시 objectURL 을 해제한다.
  useEffect(() => {
    if (!thumbnailFile) {
      setThumbnailPreview(null);
      return;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setThumbnailFile(file);
    // 같은 파일을 다시 골라도 change 가 발생하도록 초기화
    e.target.value = "";
  };

  const clearThumbnailFile = () => setThumbnailFile(null);

  // 표시 우선순위: 선택 파일 > images[0] > legacy fallback
  const thumbnailSrc = thumbnailPreview ?? images[0] ?? legacyThumbnail;

  // RichTextEditor 는 uploadImage 참조가 바뀌면 에디터를 재생성한다 — useCallback 으로 고정.
  // 본문 이미지 업로드가 끝나기 전에 저장하면 아직 본문에 삽입되지 않은 이미지가 누락된 채
  // PATCH 되므로, 진행 중인 업로드 수를 세어 저장을 막는다 (동시 업로드 대비 count).
  const uploadDetailImage = useCallback(async (file: File): Promise<string> => {
    detailImageUploadingRef.current += 1;
    setDetailImageUploading(detailImageUploadingRef.current);
    try {
      const { data } = await toursService.uploadImage(file);
      return data.url;
    } finally {
      detailImageUploadingRef.current -= 1;
      setDetailImageUploading(detailImageUploadingRef.current);
    }
  }, []);

  // 정규화(source="api")도 화면 state 에는 반영해 에디터와 폼을 어긋나지 않게 하되, dirty 로는 세지 않는다.
  const handleDetailedContentChange = useCallback((html: string, source?: RichTextChangeSource) => {
    if (source === "user") detailedContentDirtyRef.current = true;
    setForm((prev) => ({ ...prev, detailed_content: html }));
  }, []);

  const guideOptions = useMemo(() => {
    const names = instructors.map((i) => i.name).filter((n) => !form.guide_names.includes(n));
    const unique = Array.from(new Set(names));
    return [{ value: "", label: "강사 선택…" }, ...unique.map((n) => ({ value: n, label: n }))];
  }, [instructors, form.guide_names]);

  // ─────────────────────────────────────────
  // 저장
  // ─────────────────────────────────────────
  const handleSave = async () => {
    if (!id) return;
    if (savingRef.current) return; // 중복 저장(헤더/푸터 버튼 동시 클릭) 차단
    // 본문 이미지 업로드가 끝나기 전 저장하면 그 이미지가 빠진 채 저장된다. 버튼 disabled 와
    // 별개로 동기 차단해, 리렌더 전에 눌린 클릭도 PATCH·navigate 까지 가지 못하게 한다.
    if (detailImageUploadingRef.current > 0)
      return alert("본문 이미지 업로드가 끝난 뒤 저장해주세요.");
    if (!form.name.trim()) return alert("투어 제목을 입력하세요.");
    if (!form.location.trim()) return alert("투어 장소를 입력하세요.");
    if (!form.price || Number(form.price) < 1) return alert("예약금(가격)을 1원 이상 입력하세요.");
    if (!form.max_participants || Number(form.max_participants) < 1)
      return alert("최대 인원을 1명 이상 입력하세요.");
    if (form.guide_names.length < 1) return alert("인솔강사를 최소 1명 지정하세요.");

    // 일정 검증: Day 별 날짜 필수 + 세부 일정 최소 1개
    for (let i = 0; i < schedule.length; i++) {
      const day = schedule[i];
      if (!day.date) return alert(`Day ${i + 1}의 날짜를 입력하세요.`);
      if (day.items.every((it) => it.trim() === ""))
        return alert(`Day ${i + 1}의 세부 일정을 최소 1개 입력하세요.`);
    }

    // 포함/불포함 검증: 항목명이 비어 있는 항목이 남아있으면 저장 차단
    if (includeItems.some((it) => it.label.trim() === ""))
      return alert("항목명이 비어 있는 포함/불포함 항목이 있습니다. 입력하거나 삭제해주세요.");

    // 빈 세부 일정은 저장에서 제외
    const cleanedSchedule = schedule.map((d) => ({
      ...d,
      items: d.items.filter((it) => it.trim() !== ""),
    }));
    const cleanedIncludeItems = includeItems;

    const payload: TourUpdateRequest = {
      name: form.name.trim(),
      location: form.location.trim(),
      price: Number(form.price),
      currency: form.currency,
      balance_usd: Number(form.balance_usd || 0),
      balance_currency: form.balance_currency,
      single_room_surcharge: Number(form.single_room_surcharge || 0),
      min_participants: Number(form.min_participants || 1),
      max_participants: Number(form.max_participants),
      intro_desc: form.intro_desc,
      qualifications: form.qualifications,
      guide_names: form.guide_names,
      schedule: cleanedSchedule,
      include_items: cleanedIncludeItems,
    };
    if (form.start_date) payload.start_date = form.start_date;
    if (form.end_date) payload.end_date = form.end_date;
    // 상세 내용은 사용자가 편집했을 때만 보낸다 (partial update — 생략 = 기존값 유지).
    if (detailedContentDirtyRef.current) payload.detailed_content = form.detailed_content;

    savingRef.current = true;
    setSaving(true);
    try {
      // 새 썸네일이 있으면 먼저 업로드한다. 실패하면 PATCH 를 보내지 않는다 —
      // 부분 저장은 "썸네일이 바뀐 줄 아는" 오해를 만든다.
      if (thumbnailFile) {
        let uploadedUrl: string;
        try {
          const { data } = await toursService.uploadImage(thumbnailFile);
          uploadedUrl = data?.url;
          if (!uploadedUrl) throw new Error("upload response missing url");
        } catch {
          alert("대표 이미지 업로드에 실패했습니다. 다시 시도해주세요.");
          return;
        }
        // images[0] 만 교체하고 나머지 갤러리 이미지는 그대로 보존한다.
        payload.images = [uploadedUrl, ...images.slice(1)];
      }
      // 썸네일 미선택 시 `images` 키를 아예 보내지 않는다 (partial update = 기존값 유지).

      await toursService.update(Number(id), payload as Record<string, unknown>);
      alert("투어 정보가 저장되었습니다.");
      navigate(-1);
    } catch (e) {
      const data = (
        e as {
          response?: {
            data?: { guide_names?: { message?: string }; detail?: string; error?: string };
          };
        }
      )?.response?.data;
      const msg =
        data?.guide_names?.message ||
        data?.detail ||
        data?.error ||
        "저장에 실패했습니다. 입력값을 확인해주세요.";
      alert(typeof msg === "string" ? msg : "저장에 실패했습니다.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!id) return;
    if (!window.confirm("이 투어를 비활성화하시겠습니까?")) return;
    try {
      await toursService.disable(Number(id));
      alert("투어가 비활성화되었습니다.");
      navigate(-1);
    } catch {
      alert("비활성화에 실패했습니다.");
    }
  };

  // ─────────────────────────────────────────
  // 렌더
  // ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page__loading">불러오는 중...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="admin-page">
        <div className="tour-edit__notfound">
          <p>투어 정보를 찾을 수 없습니다.</p>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            뒤로가기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <ContentHeader
        title="투어 상세 / 수정"
        description={`#${id} ${tourName} ${isApproved == null ? "" : isApproved ? "(승인 완료)" : "(승인 대기)"}`}
        actions={[
          <Button key="back" variant="outline" size="sm" onClick={() => navigate(-1)}>
            ← 뒤로가기
          </Button>,
          <Button key="disable" variant="danger" size="sm" onClick={handleDisable}>
            비활성화
          </Button>,
          <Button
            key="save"
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || detailImageUploading > 0}
          >
            {saving ? "저장 중..." : "저장"}
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        {/* 기본 정보 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">기본 정보</h3>
          <div className="tour-edit__grid">
            <Input
              label="투어 제목"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
            />
            <Input
              label="투어 장소"
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
            />
            <Input
              label="시작일"
              type="date"
              value={form.start_date}
              onChange={(e) => setField("start_date", e.target.value)}
            />
            <Input
              label="종료일"
              type="date"
              value={form.end_date}
              onChange={(e) => setField("end_date", e.target.value)}
            />
            <Input
              label="최소 출발 인원"
              type="number"
              min={1}
              value={form.min_participants}
              onChange={(e) => setField("min_participants", e.target.value)}
            />
            <Input
              label="최대 인원(정원)"
              type="number"
              min={1}
              value={form.max_participants}
              onChange={(e) => setField("max_participants", e.target.value)}
            />
          </div>
        </section>

        {/* 비용 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">비용</h3>
          <div className="tour-edit__grid">
            <Input
              label="예약금(가격)"
              type="number"
              min={1}
              value={form.price}
              onChange={(e) => setField("price", e.target.value)}
            />
            <Select
              label="예약금 통화"
              options={CURRENCY_OPTIONS.slice(0, 1)}
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            />
            <Input
              label="잔금"
              type="number"
              min={0}
              value={form.balance_usd}
              onChange={(e) => setField("balance_usd", e.target.value)}
            />
            <Select
              label="잔금 통화"
              options={CURRENCY_OPTIONS}
              value={form.balance_currency}
              onChange={(e) => setField("balance_currency", e.target.value)}
            />
            <Input
              label="1인실 추가요금"
              type="number"
              min={0}
              value={form.single_room_surcharge}
              onChange={(e) => setField("single_room_surcharge", e.target.value)}
            />
          </div>
        </section>

        {/* 인솔강사 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">인솔강사</h3>
          <div className="tour-edit__guide-add">
            <Select
              options={guideOptions}
              value={guideToAdd}
              onChange={(e) => {
                addGuide(e.target.value);
                setGuideToAdd("");
              }}
            />
            <span className="tour-edit__guide-hint">
              강사를 선택하면 아래에 추가됩니다 (최대 5명)
            </span>
          </div>
          <div className="tour-edit__chips">
            {form.guide_names.length === 0 ? (
              <span className="tour-edit__chips-empty">지정된 인솔강사가 없습니다.</span>
            ) : (
              form.guide_names.map((name) => (
                <span key={name} className="tour-edit__chip">
                  {name}
                  <button
                    type="button"
                    className="tour-edit__chip-remove"
                    onClick={() => removeGuide(name)}
                    aria-label={`${name} 제거`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
        </section>

        {/* 대표 이미지 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">대표 이미지</h3>
          <div className="tour-edit__thumbnail">
            <div className="tour-edit__thumbnail-slot">
              {thumbnailSrc ? (
                <img
                  className="tour-edit__thumbnail-img"
                  src={thumbnailSrc}
                  alt="대표 이미지 미리보기"
                />
              ) : (
                <span className="tour-edit__thumbnail-empty">등록된 대표 이미지가 없습니다.</span>
              )}
              <span className="tour-edit__thumbnail-badge">썸네일</span>
            </div>
            <div className="tour-edit__thumbnail-actions">
              {/* input 을 label 앞에 둬야 포커스 링 형제 선택자(input:focus + label)가 동작한다 */}
              <input
                id="tour-thumbnail-input"
                data-testid="tour-thumbnail-input"
                className="tour-edit__file-input"
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
              />
              <label className="tour-edit__file-label" htmlFor="tour-thumbnail-input">
                이미지 선택
              </label>
              {thumbnailFile && (
                <>
                  <span className="tour-edit__thumbnail-filename">{thumbnailFile.name}</span>
                  <button
                    type="button"
                    className="tour-edit__thumbnail-clear"
                    onClick={clearThumbnailFile}
                  >
                    선택 취소
                  </button>
                </>
              )}
              <p className="tour-edit__thumbnail-hint">
                저장 시 업로드되어 대표 이미지로 반영됩니다. 나머지 이미지는 그대로 유지됩니다.
              </p>
            </div>
          </div>
        </section>

        {/* 소개 / 상세 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">투어 소개 한마디</h3>
          <div className="tour-edit__field">
            <textarea
              className="tour-edit__textarea"
              rows={2}
              value={form.intro_desc}
              onChange={(e) => setField("intro_desc", e.target.value)}
            />
          </div>
        </section>

        {/* 참가 자격사항 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">참가 자격사항</h3>
          <div className="tour-edit__field">
            <textarea
              className="tour-edit__textarea"
              rows={4}
              value={form.qualifications}
              onChange={(e) => setField("qualifications", e.target.value)}
              placeholder={"한 줄에 하나씩 입력하세요.\n예) ✅ 프리다이빙 자격증 보유자"}
            />
          </div>
        </section>

        {/* 상세 내용 */}
        <section className="tour-edit__section">
          <h3 className="tour-edit__section-title">상세 내용</h3>
          <div className="tour-edit__field">
            <RichTextEditor
              value={form.detailed_content}
              onChange={handleDetailedContentChange}
              uploadImage={uploadDetailImage}
              placeholder="투어 상세 내용을 입력해주세요"
            />
          </div>
        </section>

        {/* 일정표 */}
        <section className="tour-edit__section">
          <div className="tour-edit__section-head">
            <h3 className="tour-edit__section-title">일정</h3>
            <Button variant="outline" size="sm" onClick={addScheduleDay}>
              + 날짜 추가
            </Button>
          </div>
          {schedule.length === 0 ? (
            <p className="tour-edit__empty">등록된 일정이 없습니다.</p>
          ) : (
            <div className="tour-edit__schedule">
              {schedule.map((day, i) => (
                <div key={i} className="tour-edit__schedule-day">
                  <div className="tour-edit__schedule-head">
                    <span className="tour-edit__schedule-badge">Day {day.day}</span>
                    <input
                      type="date"
                      className="tour-edit__schedule-date-input"
                      value={day.date ?? ""}
                      onChange={(e) => setScheduleDate(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="tour-edit__day-remove"
                      onClick={() => removeScheduleDay(i)}
                    >
                      날짜 삭제
                    </button>
                  </div>
                  <ul className="tour-edit__schedule-items">
                    {day.items.map((item, j) => (
                      <li key={j} className="tour-edit__schedule-item">
                        <input
                          type="text"
                          className="tour-edit__schedule-item-input"
                          value={item}
                          onChange={(e) => setScheduleItem(i, j, e.target.value)}
                        />
                        <button
                          type="button"
                          className="tour-edit__item-remove"
                          onClick={() => removeScheduleItem(i, j)}
                          aria-label="세부 일정 삭제"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="tour-edit__item-add"
                    onClick={() => addScheduleItem(i)}
                  >
                    + 세부 일정 추가
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 포함 / 불포함 사항 */}
        <section className="tour-edit__section">
          <div className="tour-edit__section-head">
            <h3 className="tour-edit__section-title">포함 / 불포함 사항</h3>
            <Button variant="outline" size="sm" onClick={addIncludeItem}>
              + 항목 추가
            </Button>
          </div>
          {includeItems.length === 0 ? (
            <p className="tour-edit__empty">등록된 항목이 없습니다.</p>
          ) : (
            <ul className="tour-edit__include-list">
              {includeItems.map((it, i) => (
                <li
                  key={i}
                  className={`tour-edit__include-item ${it.include ? "tour-edit__include-item--yes" : "tour-edit__include-item--no"}`}
                >
                  <input
                    type="text"
                    className="tour-edit__include-input"
                    value={it.label}
                    placeholder="항목명"
                    onChange={(e) => setIncludeLabel(i, e.target.value)}
                  />
                  <div className="tour-edit__include-actions">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={it.include}
                      aria-label={`${it.label} ${it.include ? "포함" : "불포함"}`}
                      className={`tour-edit__toggle ${it.include ? "tour-edit__toggle--on" : ""}`}
                      onClick={() => toggleIncludeItem(i)}
                    >
                      <span className="tour-edit__toggle-knob" />
                      <span className="tour-edit__toggle-text">
                        {it.include ? "포함" : "불포함"}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="tour-edit__item-remove"
                      onClick={() => removeIncludeItem(i)}
                      aria-label="항목 삭제"
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="tour-edit__footer">
          <Button variant="outline" onClick={() => navigate(-1)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || detailImageUploading > 0}
          >
            {saving ? "저장 중..." : "저장"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourEdit;
